import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MASAR } from "@/constants/testIds";
import { SectionLabel } from "@/components/masar/HowItWorks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Support() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);
    if (!form.name || !form.email || !form.message) {
      setErr(t("support.errRequired"));
      toast.error(t("support.errRequired"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setOk(true);
      toast.success(t("support.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      const msg = e?.response?.data?.detail || t("support.errGeneric");
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="support"
      data-testid={MASAR.sectionSupport}
      className="relative py-24 sm:py-32 bg-neutral-900/40 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionLabel index="07" label={t("support.chapter")} />
        <div className="mt-4 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {t("support.titleA")}
              <br />
              {t("support.titleB")}
            </h2>
            <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
              {t("support.lede")}
            </p>

            <div className="mt-8 space-y-3">
              <ContactRow
                icon={Mail}
                label={t("support.emailLabel")}
                value="emailsupport@masar.ltd"
                href="mailto:emailsupport@masar.ltd"
                testid={MASAR.contactEmailLink}
              />
              <ContactRow
                icon={Phone}
                label={t("support.phoneLabel")}
                value={t("support.phoneValue")}
              />
              <ContactRow icon={MapPin} label={t("support.regionLabel")} value={t("support.regionValue")} />
            </div>

            <div className="mt-10 rounded-2xl border border-white/10 bg-neutral-950 p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                {t("support.domainLabel")}
              </div>
              <div className="mt-1 text-xl font-semibold">masar.ltd</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid={MASAR.contactForm}
              onSubmit={submit}
              className="rounded-3xl border border-white/10 bg-neutral-950 p-6 sm:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("support.nameLabel")} required>
                  <Input
                    data-testid={MASAR.contactName}
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder={t("support.namePh")}
                    className="h-11 rounded-xl"
                  />
                </Field>
                <Field label={t("support.emailFieldLabel")} required>
                  <Input
                    data-testid={MASAR.contactEmail}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder={t("support.emailPh")}
                    className="h-11 rounded-xl"
                  />
                </Field>
                <Field label={t("support.phoneFieldLabel")}>
                  <Input
                    data-testid={MASAR.contactPhone}
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder={t("support.phonePh")}
                    className="h-11 rounded-xl"
                  />
                </Field>
                <Field label={t("support.subjectLabel")}>
                  <Input
                    data-testid={MASAR.contactSubject}
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    placeholder={t("support.subjectPh")}
                    className="h-11 rounded-xl"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <Field label={t("support.messageLabel")} required>
                  <Textarea
                    data-testid={MASAR.contactMessage}
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder={t("support.messagePh")}
                    rows={5}
                    className="rounded-xl resize-none"
                  />
                </Field>
              </div>

              {ok && (
                <div
                  data-testid={MASAR.contactSuccess}
                  className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 text-sm"
                >
                  {t("support.success")}
                </div>
              )}
              {err && (
                <div
                  data-testid={MASAR.contactError}
                  className="mt-4 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm"
                >
                  {err}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-neutral-500">
                  {t("support.consent")}
                </p>
                <Button
                  data-testid={MASAR.contactSubmit}
                  type="submit"
                  disabled={loading}
                  className="rounded-full h-11 px-6 bg-[#FF6A1A] hover:bg-[#ff7d38] text-white masar-pill-shadow"
                >
                  {loading ? t("support.sending") : (
                    <>
                      {t("support.send")}
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-mono uppercase tracking-widest text-neutral-400">
        {label}
        {required && <span className="text-[#DC2626] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      {...(href ? { href, "data-testid": testid } : {})}
      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-neutral-950 px-4 py-3 hover:border-[#FF6A1A] transition-colors"
    >
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#FF6A1A]">
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
          {label}
        </div>
        <div className="font-medium truncate">{value}</div>
      </div>
    </Wrapper>
  );
}
